// The affordability path (plan §3b) — the teacher who cannot buy the copy.
//
// WHY IT IS NOT BEHIND THE SAME GATE. The notice above promises that funding
// will not be the roadblock. Putting the $8 in front of every door would make
// the gate itself the roadblock, and the promise a lie in the one case it was
// written for. So this asks for something other than money: sign in with a
// school or district address, and a human checks that the domain is a school.
//
// `requestReviewAccess` stores the address's DOMAIN and nothing else — the
// whole of what an admin needs to answer "is this a school?". The address
// itself lives with the identity provider (WorkOS carries it), which is why
// the copy makes no we-don't-keep-your-address claim: it would be untrue.

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@bindery/convex/_generated/api";
import { Button } from "@bindery/ui/components/ui/button";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { Textarea } from "@bindery/ui/components/ui/textarea";
import { signIn } from "@/lib/auth";
import { errorMessage } from "@/lib/convex-error";
import { useLogger } from "@/logging";
import { useCoarseNow } from "@/lib/use-coarse-now";

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <section className="mt-12 border-t border-[--rule] pt-10">
      <h2 className="text-muted-foreground font-serif text-[0.75rem] tracking-[0.35em] uppercase">
        If you can’t buy a copy
      </h2>
      <p className="text-foreground mt-4 font-serif text-[1.05rem] leading-relaxed">
        Sign in with your school or district email and ask for a review copy: we
        check that the domain belongs to a school, and open the whole
        translation to you for long enough to decide whether you want it for
        your class.
      </p>
      <p className="text-muted-foreground mt-3 font-serif text-[0.95rem] leading-relaxed">
        A person reads every one of these, so it is not instant.
      </p>
      {children}
    </section>
  );
}

export function ReviewAccessRequest() {
  const now = useCoarseNow();
  const status = useQuery(api.classroom.myStatus, { now });
  const request = useMutation(api.classroom.requestReviewAccess);
  const log = useLogger();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);

  // A teacher who already holds a copy has no use for this, and showing it to
  // them reads as an offer to refund the one they bought.
  if (status?.entitled === true) return null;

  const already =
    status?.inquiries.some((row) => row.kind === "review_access") === true;

  if (sent || already) {
    return (
      <Frame>
        <p className="text-foreground mt-6 border border-[--rule] px-6 py-5 font-serif text-[1.05rem] leading-relaxed">
          Your request is in. We will write to you once someone has checked the
          domain — and if anything is unclear, we will ask rather than decline.
        </p>
      </Frame>
    );
  }

  if (status?.authed !== true) {
    return (
      <Frame>
        <Button
          className="mt-6"
          onClick={() => {
            signIn();
          }}
        >
          Sign in with your school email
        </Button>
      </Frame>
    );
  }

  const onSubmit = async (form: HTMLFormElement): Promise<void> => {
    const data = new FormData(form);
    // `FormData.get` is `string | File | null`, and `String(File)` is the
    // useless "[object File]" — a text field is not the only thing that can
    // carry a form entry's name.
    const text = (name: string): string => {
      const value = data.get(name);
      return typeof value === "string" ? value : "";
    };
    const students = Number(data.get("students"));
    setBusy(true);
    try {
      await request({
        teacherName: text("teacherName"),
        school: text("school"),
        role: text("role"),
        students:
          Number.isFinite(students) && students > 0 ? students : undefined,
        message: text("message"),
      });
      // Never the domain: it names the school as surely as the school field
      // does (logging-strategy).
      log.event("classroom.review_access_submitted", {});
      setSent(true);
    } catch (error) {
      toast.error("That didn't go through", {
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Frame>
        <Button
          variant="outline"
          className="mt-6"
          onClick={() => {
            setOpen(true);
          }}
        >
          Ask for a review copy
        </Button>
      </Frame>
    );
  }

  return (
    <Frame>
      <form
        className="mt-6 space-y-4 border border-[--rule] px-6 py-6"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit(event.currentTarget);
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="ra-name" className="text-foreground font-serif">
            Your name
          </Label>
          <Input id="ra-name" name="teacherName" required maxLength={120} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ra-school" className="text-foreground font-serif">
            School or district
          </Label>
          <Input id="ra-school" name="school" required maxLength={200} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ra-role" className="text-foreground font-serif">
              What you teach
            </Label>
            <Input
              id="ra-role"
              name="role"
              maxLength={120}
              placeholder="9th grade English"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ra-students" className="text-foreground font-serif">
              How many students
            </Label>
            <Input
              id="ra-students"
              name="students"
              type="number"
              min={1}
              max={500}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ra-message" className="text-foreground font-serif">
            Anything else
          </Label>
          <Textarea id="ra-message" name="message" rows={3} maxLength={2000} />
        </div>
        <Button type="submit" disabled={busy}>
          {busy && (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          )}
          Send the request
        </Button>
      </form>
    </Frame>
  );
}
